import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { CloudUpload, Delete, Description, Folder } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { uploadService } from '../services/uploadService';
import { UploadedFile } from '../types';
import { Header } from '../components/layout/Header';
import { format } from 'date-fns';

export const AuctionFilesPage: React.FC = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = async () => {
    try {
      setLoading(true);
      const data = await uploadService.getUploads();
      // Sort by uploadedAt descending (newest first)
      const sortedData = data.sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
      setUploads(sortedData);
    } catch (err: any) {
      console.error('Failed to load uploads:', err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni a feltöltött fájlokat');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setError('');
    setUploading(true);

    try {
      const uploadedFile = await uploadService.uploadFile(file);
      await loadUploads();

      // Redirect to mapping editor
      navigate(`/mapping-editor?uploadId=${uploadedFile.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fájl feltöltési hiba');
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: uploading,
  });

  const handleDeleteUpload = async (uploadId: number, fileName: string) => {
    if (!window.confirm(`Biztosan törölni szeretnéd a "${fileName}" fájlt?`)) {
      return;
    }

    try {
      const response = await uploadService.deleteUpload(uploadId);

      // Remove from local state immediately for better UX
      setUploads(prev => prev.filter(u => u.id !== uploadId));

      // Show success message
      if (response?.deletedPredictionsCount && response.deletedPredictionsCount > 0) {
        alert(`Fájl törölve. ${response.deletedPredictionsCount} kapcsolódó árazás is törölve lett.`);
      }
    } catch (err: any) {
      console.error('Failed to delete upload:', err);
      const errorMessage = err.response?.data?.error ||
                          err.response?.data?.message ||
                          'Nem sikerült törölni a fájlt';
      alert(errorMessage);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSED':
      case 'UPLOADED':
        return 'success';
      case 'PROCESSING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Folder /> Aukciós Fájlok
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Feltöltött Excel követelésállomány fájlok kezelése
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Upload Area */}
        <Paper
          sx={{
            p: 4,
            mb: 3,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1,
            transition: 'all 0.3s ease',
          }}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Box sx={{ textAlign: 'center' }}>
            {uploading ? (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Feltöltés folyamatban...
                </Typography>
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Engedje el a fájlt a feltöltéshez' : 'Húzza ide a fájlt vagy kattintson a kiválasztáshoz'}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Támogatott formátumok: .xlsx, .xls
                </Typography>
                <Button variant="contained" component="span" disabled={uploading}>
                  Fájl kiválasztása
                </Button>
              </>
            )}
          </Box>
        </Paper>

        {/* Uploaded Files List */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Feltöltött Fájlok ({uploads.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : uploads.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Még nincs feltöltött fájl.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Töltsön fel egy Excel fájlt a fenti területre.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Fájlnév</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Méret</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mapping</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Státusz</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Feltöltve</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Műveletek</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploads.map((upload) => (
                    <TableRow
                      key={upload.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/auction-files/${upload.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                          {upload.fileName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(upload.fileSize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {upload.mappingTemplate ? (
                          <Chip
                            label={upload.mappingTemplate.name}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            Nincs mapping
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: getStatusColor(upload.status) === 'success' ? 'success.main' :
                                   getStatusColor(upload.status) === 'warning' ? 'warning.main' :
                                   getStatusColor(upload.status) === 'error' ? 'error.main' : 'text.secondary',
                            fontWeight: 500,
                          }}
                        >
                          {upload.status}
                        </Typography>
                        {upload.errorMessage && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {upload.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(upload.uploadedAt), 'yyyy-MM-dd HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUpload(upload.id, upload.fileName);
                            }}
                            title="Törlés"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </>
  );
};



